/* GS://PROTOCOLO
 * Arquivo: src/App.js
 * Versão: 20.11 – FIX: Roteamento Inteligente (Smart Routing)
 * Impacto da Versão: [Rotas e Redirecionamento de Login]
 * Data: 16/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [FIX]: Implementada função `getInitialRoute` para decidir o destino pós-login.
 * - [LOGIC]: Usuários sem acesso ao Dashboard são redirecionados para PDV, Pedidos ou Clientes.
 * - [MAINTENANCE]: Resolve o erro "Missing permissions" ao logar com perfis restritos.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { auth, db } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  DollarSign,
  Package,
  Users,
  ShoppingCart,
  Menu as MenuIcon,
  Bell,
  CreditCard,
  Archive,
  ClipboardList,
  Building,
  ChevronsUpDown,
  Plus,
  Loader,
  LogOut,
  ShieldCheck,
  FileUp,
  PiggyBank,
  ClipboardCheck,
  LayoutDashboard,
  CalendarMinus,
  CalendarPlus,
  BarChart2,
  History,
  Settings
} from 'lucide-react';

import Modal from './components/ui/Modal';

// PÁGINAS
import FinancialDashboardPage from './pages/finance/FinancialDashboardPage';

import POSPage from './pages/POSPage';
import OrdersPage from './pages/OrdersPage';
import ProductsPage from './pages/ProductsPage';
import CategoriesPage from './pages/CategoriesPage';
import SuppliersPage from './pages/SuppliersPage';
import CustomersPage from './pages/CustomersPage';
import ImportProductsPage from './pages/ImportProductsPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import GrantAdminPage from './pages/admin/GrantAdminPage';
import FinancialRecordsPage from './pages/FinancialRecordsPage';
import FinancePage from './pages/FinancePage';
import AccountsPayablePage from './pages/AccountsPayablePage';
import AccountsReceivablePage from './pages/AccountsReceivablePage';
import RentalsPage from './pages/RentalsPage';
import CashierReportPage from './pages/CashierReportPage';
import NFEntryPage from './pages/NFEntryPage';
import LotsListPage from './pages/lots/LotsListPage';
import LotDetailPage from './pages/lots/LotDetailPage';
import LotBathPage from './pages/lots/LotBathPage';
import StockPage from './pages/StockPage';
import NFHistoryPage from './pages/NFHistoryPage';
import PurchasesPage from './pages/PurchasesPage';
import NewPurchasePage from './pages/NewPurchasePage';
import CostAllocationPage from './pages/CostAllocationPage';
import EditPurchasePage from './pages/EditPurchasePage';
import AssemblyOrdersPage from './pages/assembly/AssemblyOrdersPage.js';
import AssemblyOrderDetailPage from './pages/assembly/AssemblyOrderDetailPage';
import AssemblyOrderFormPage from './pages/assembly/AssemblyOrderFormPage';
import AssemblyOrderComponentsPage from './pages/assembly/AssemblyOrderComponentsPage';
import LoginPage from './pages/LoginPage';
import PrintReceiptPage from './pages/PrintReceiptPage';
import PushCenterPage from './pages/admin/PushCenterPage';
import SystemConfigsPage from './pages/admin/SystemConfigsPage';


function MainAppContent({ openModal, closeModal }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isFinanceiroOpen, setIsFinanceiroOpen] = useState(true);
  const [isCadastrosOpen, setIsCadastrosOpen] = useState(false);
  const [isComprasOpen, setIsComprasOpen] = useState(false);
  const [isLegadoOpen, setIsLegadoOpen] = useState(false);

  const navigate = useNavigate();

  // [GS-FIX]: Função inteligente para decidir a rota inicial baseada nas permissões
  const getInitialRoute = (role, permissions) => {
    // 1. Admin ou quem tem acesso explícito ao Dashboard
    if (role === 'admin' || permissions?.viewDashboard) return '/dashboard';

    // 2. Prioridade Operacional: Frente de Caixa (Vendedores/Freelancers)
    if (permissions?.accessPOS) return '/pos';

    // 3. Gestão de Pedidos
    if (permissions?.manageOrders) return '/orders';

    // 4. Gestão de Clientes
    if (permissions?.manageCustomers) return '/customers';

    // 5. Cadastros
    if (permissions?.manageProducts) return '/products';

    // Fallback final (vai dar erro de permissão, mas é o comportamento padrão)
    return '/dashboard';
  };

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, 'staff', currentUser.uid);

        try {
            const snap = await getDoc(userDocRef);

            if (snap.exists()) {
              const data = snap.data();
              setUserData(data);

              // [GS-FIX]: Redirecionamento inteligente ao carregar a raiz ou login
              if (window.location.pathname === '/' || window.location.pathname === '/login') {
                const targetRoute = getInitialRoute(data.role, data.permissions);
                navigate(targetRoute);
              }
            } else {
              // Usuário autenticado mas sem registro na collection 'staff'
              await signOut(auth);
              setUser(null);
              setUserData(null);
              navigate('/login');
            }
        } catch (error) {
            console.error("Erro ao buscar dados do usuário:", error);
            // Em caso de erro crítico (ex: permissão negada na própria leitura do staff), faz logout
            await signOut(auth);
            setUser(null);
            navigate('/login');
        }

      } else {
        setUser(null);
        setUserData(null);
        if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/print')) {
          navigate('/login');
        }
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, [navigate]);

  const NavLink = ({ to, label, icon: Icon, isSublink = false }) => {
    const location = useLocation();
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`flex items-center w-full px-4 py-3 text-sm rounded-lg transition-colors duration-200 ${
          isSublink ? 'pl-11' : ''
        } ${
          isActive
            ? 'bg-brand-gold text-brand-black font-semibold shadow-lg'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
        }`}
      >
        {Icon && <Icon className="w-5 h-5 mr-3" />}
        <span>{label}</span>
      </Link>
    );
  };

  useEffect(() => {
    const handleResize = () => setSidebarOpen(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const location = useLocation();
  if (location.pathname.startsWith('/print/')) {
    return (
      <Routes>
        <Route path="/print/receipt/:saleId" element={<PrintReceiptPage />} />
      </Routes>
    );
  }

  if (!isAuthReady) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-black text-white">
        <Loader className="animate-spin h-10 w-10 text-brand-gold" />
        <span className="ml-4 text-xl">Verificando permissões...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  const userRole = userData?.role || 'guest';
  const userPermissions = userData?.permissions || {};

  const isAdmin = userRole === 'admin';
  const canViewDashboard = isAdmin || userPermissions.viewDashboard;
  const canAccessPOS = isAdmin || userPermissions.accessPOS;
  const canManageOrders = isAdmin || userPermissions.manageOrders;
  const canAccessFinanceiro = isAdmin || userPermissions.accessFinanceiro;
  const canManageRentals = isAdmin || userPermissions.manageRentals;
  const canAccessCashierReports = isAdmin || userPermissions.accessCashierReports;
  const canViewCadastros = isAdmin || userPermissions.manageProducts || userPermissions.manageSuppliers || userPermissions.manageCategories;
  const canViewCompras = isAdmin || userPermissions.managePurchases;
  const canManageCustomers = isAdmin || userPermissions.manageCustomers;
  const canManageUsers = isAdmin || userPermissions.manageUsers;

  return (
    <div className="flex h-screen bg-brand-black font-sans text-white">
      <aside className={`bg-brand-dark transition-all duration-300 flex flex-col z-40 ${isSidebarOpen ? 'w-64' : 'w-0 overflow-hidden'}`}>
        <div className="flex items-center justify-center h-24 border-b border-gray-700 flex-shrink-0">
          <img src="/logo_perfil.jpg" alt="Logo GoldenSisters" className="h-16 rounded-md" />
        </div>

        <nav className="p-4 space-y-2 flex-grow overflow-y-auto">

          {canViewDashboard && <NavLink to="/dashboard" label="Visão Geral" icon={LayoutDashboard} />}

          {canAccessPOS && <NavLink to="/pos" label="Frente de Caixa" icon={CreditCard} />}
          {canManageOrders && <NavLink to="/orders" label="Pedidos" icon={ShoppingCart} />}

          {canAccessFinanceiro && (
            <div>
              <button
                onClick={() => setIsFinanceiroOpen(!isFinanceiroOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-3" />
                  Financeiro
                </div>
                <ChevronsUpDown className={`w-5 h-5 transition-transform ${isFinanceiroOpen ? 'rotate-180' : ''}`} />
              </button>

              {isFinanceiroOpen && (
                <div className="py-1 space-y-1 mt-1 bg-gray-900 rounded-lg">
                  <NavLink to="/financeiro" label="Extrato de Lançamentos" icon={PiggyBank} isSublink />
                  <NavLink to="/contas-a-pagar" label="Contas a Pagar" icon={CalendarMinus} isSublink />
                  <NavLink to="/contas-a-receber" label="Contas a Receber" icon={CalendarPlus} isSublink />
                  {canManageRentals && <NavLink to="/rentals" label="Aluguéis" icon={ClipboardCheck} isSublink />}
                  <NavLink to="/financeiro/relatorio" label="Relatório Personalizado" icon={ClipboardList} isSublink />
                  {canAccessCashierReports && <NavLink to="/cashier-reports" label="Fechamento de Caixa" icon={DollarSign} isSublink />}
                </div>
              )}
            </div>
          )}

          {canViewCadastros && (
            <div>
              <button
                onClick={() => setIsCadastrosOpen(!isCadastrosOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center">
                  <Package className="w-5 h-5 mr-3" />
                  Cadastros
                </div>
                <ChevronsUpDown className={`w-5 h-5 transition-transform ${isCadastrosOpen ? 'rotate-180' : ''}`} />
              </button>
              {isCadastrosOpen && (
                <div className="py-1 space-y-1 mt-1 bg-gray-900 rounded-lg">
                  {isAdmin || userPermissions.manageProducts ? <NavLink to="/products" label="Produtos" icon={Package} isSublink /> : null}
                  {isAdmin || userPermissions.manageProducts ? <NavLink to="/products/import" label="Importar Produtos" icon={FileUp} isSublink /> : null}
                  {isAdmin || userPermissions.manageCategories ? <NavLink to="/categories" label="Categorias" icon={ClipboardList} isSublink /> : null}
                  {isAdmin || userPermissions.manageSuppliers ? <NavLink to="/suppliers" label="Fornecedores" icon={Building} isSublink /> : null}
                </div>
              )}
            </div>
          )}

          {canViewCompras && (
            <div>
              <button
                onClick={() => setIsComprasOpen(!isComprasOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center">
                  <Archive className="w-5 h-5 mr-3" />
                  Compras & Estoque
                </div>
                <ChevronsUpDown className={`w-5 h-5 transition-transform ${isComprasOpen ? 'rotate-180' : ''}`} />
              </button>
              {isComprasOpen && (
                <div className="py-1 space-y-1 mt-1 bg-gray-900 rounded-lg">
                   <NavLink to="/nf/entrada" label="Entrada NF" icon={ClipboardList} isSublink />
                   <NavLink to="/lotes" label="Lotes" icon={Archive} isSublink />
                   <NavLink to="/estoque" label="Visão de Estoque" icon={Package} isSublink />
                   <NavLink to="/nf/historico" label="Histórico NF" icon={History} isSublink />
                   <NavLink to="/assembly/orders" label="Montagem" icon={ClipboardCheck} isSublink />
                </div>
              )}
            </div>
          )}

          {(isAdmin || userPermissions.managePurchases) && (
            <div>
              <button
                onClick={() => setIsLegadoOpen(!isLegadoOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center">
                  <Archive className="w-5 h-5 mr-3" />
                  Legado
                </div>
                <ChevronsUpDown className={`w-5 h-5 transition-transform ${isLegadoOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLegadoOpen && (
                <div className="py-1 space-y-1 mt-1 bg-gray-900 rounded-lg">
                  <NavLink to="/legacy/dashboard" label="Dashboard Antigo" icon={BarChart2} isSublink />
                  <NavLink to="/purchases/new" label="Registrar Compra (Antigo)" icon={Plus} isSublink />
                  <NavLink to="/purchases" label="Histórico Compras (Antigo)" icon={ClipboardList} isSublink />
                  <NavLink to="/cost-allocation" label="Rateio de Custo" icon={DollarSign} isSublink />
                </div>
              )}
            </div>
          )}

          {canManageCustomers && <NavLink to="/customers" label="Clientes" icon={Users} />}
          {canManageUsers && <NavLink to="/users" label="Gestão RH" icon={ShieldCheck} />}

          {isAdmin && (
            <NavLink to="/push-center" label="Central de Notificações" icon={Bell} />
          )}

          {/* [GS-NEW]: Item de Menu Configurações */}
          {isAdmin && (
            <NavLink to="/settings" label="Configurações" icon={Settings} />
          )}
        </nav>

        <footer className="p-4 border-t border-gray-700 flex-shrink-0">
          <div className="flex flex-col items-start gap-1">
            <p className="text-xs text-gray-500">&copy; {new Date().getFullYear()} GoldenSisters.<br />Direitos Reservados.</p>
          </div>
        </footer>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="flex items-center justify-between h-20 bg-brand-dark border-b border-gray-700 px-6 flex-shrink-0">
          <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 mr-2 rounded-md text-gray-400 hover:text-brand-gold">
            <MenuIcon className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="text-right mr-3 hidden md:block">
                  <p className="text-sm font-medium text-white">{userData?.name || user.email}</p>
                  <p className="text-xs text-gray-400">{userRole === 'admin' ? 'Administrador' : 'Colaborador'}</p>
                </div>
                <img className="h-10 w-10 rounded-full object-cover border-2 border-brand-gold" src="https://placehold.co/100x100/FFD700/121212?text=GS" alt="User" />
              </div>
              <button onClick={handleLogout} className="p-2 rounded-full text-gray-400 hover:bg-red-500 hover:text-white transition-colors" title="Sair">
                <LogOut className="h-5 h-5" />
              </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-brand-black">
          <Routes>
            <Route path="/" element={<FinancialDashboardPage />} />
            <Route path="/dashboard" element={<FinancialDashboardPage />} />

            <Route path="/finance/dashboard" element={<FinancialDashboardPage />} />

            {/* Rota legada aponta para o novo também para evitar quebras */}
            <Route path="/legacy/dashboard" element={<FinancialDashboardPage />} />

            <Route path="/pos" element={<POSPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/orders" element={<OrdersPage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/products" element={<ProductsPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/products/import" element={<ImportProductsPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/categories" element={<CategoriesPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/suppliers" element={<SuppliersPage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/purchases" element={<PurchasesPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/purchases/new" element={<NewPurchasePage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/cost-allocation" element={<CostAllocationPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/purchases/:purchaseId/edit" element={<EditPurchasePage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/financeiro" element={<FinancialRecordsPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/financeiro/relatorio" element={<FinancePage />} />
            <Route path="/contas-a-pagar" element={<AccountsPayablePage />} />
            <Route path="/contas-a-receber" element={<AccountsReceivablePage />} />
            <Route path="/rentals" element={<RentalsPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/cashier-reports" element={<CashierReportPage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/nf/entrada" element={<NFEntryPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/lotes" element={<LotsListPage />} />
            <Route path="/lotes/:lotId" element={<LotDetailPage />} />
            <Route path="/lotes/:lotId/banho" element={<LotBathPage />} />
            <Route path="/estoque" element={<StockPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/nf/historico" element={<NFHistoryPage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/assembly/orders" element={<AssemblyOrdersPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/assembly/orders/new" element={<AssemblyOrderFormPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/assembly/orders/:orderId" element={<AssemblyOrderDetailPage />} />
            <Route path="/assembly/orders/:orderId/components" element={<AssemblyOrderComponentsPage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/customers" element={<CustomersPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/users" element={<UserManagementPage openModal={openModal} closeModal={closeModal} />} />
            <Route path="/grant-admin" element={<GrantAdminPage openModal={openModal} closeModal={closeModal} />} />

            <Route path="/push-center" element={<PushCenterPage />} />

            {/* [GS-NEW]: Rota para Configurações */}
            <Route path="/settings" element={<SystemConfigsPage />} />

            <Route path="*" element={<FinancialDashboardPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function AppWrapper() {
  const [modalContent, setModalContent] = useState(null);
  const openModal = useCallback((content, title = '', size = '4xl') => setModalContent({ content, title, size }), []);
  const closeModal = useCallback(() => setModalContent(null), []);

  return (
    <Router>
      <Routes>
        <Route path="/print/receipt/:saleId" element={<PrintReceiptPage />} />
        <Route path="*" element={<MainAppContent openModal={openModal} closeModal={closeModal} />} />
      </Routes>
      {modalContent && (
        <Modal title={modalContent.title} size={modalContent.size} onClose={closeModal}>
          {modalContent.content}
        </Modal>
      )}
    </Router>
  );
}